import { PageBlock } from '@design-system/page-block';
import { SecondaryNavigation } from '@design-system/secondary-navigation';
import { Teaser } from '@design-system/teaser';
import { TraitGroup } from '@design-system/trait-group';
import { Typography } from '@design-system/typography';
import { VariantSelector } from '@design-system/variant-selector';
import { WhereToBuy } from '@design-system/where-to-buy';
import { BazaarVoiceReviews } from '@phx-husky/bazaarvoice-components';
import React, { FC, forwardRef, Fragment, lazy } from 'react';

import { ElementOnScreen } from '~/components/ElementOnScreen';
import { useCustomComponents } from '~/context/customComponents';
import { accordionMapper } from '~/mappers/accordion';
import { contactUsBlockMapper } from '~/mappers/contactUsBlock';
import { countryPickerMapper } from '~/mappers/countryPicker';
import { iframeMapper } from '~/mappers/iframe';
import { Image } from '~/mappers/image/models';
import { imageBlockMapper } from '~/mappers/imageBlock';
import { imagePlusTextBlockMapper } from '~/mappers/imagePlusTextBlock';
import { imageTextBlockMapper } from '~/mappers/imageTextBlock';
import { itemListingMapper } from '~/mappers/itemListing';
import { jsonRTEBlockMapper } from '~/mappers/jsonRTEBlock';
import { keyValueMapper } from '~/mappers/keyValue/keyValue';
import { ldsBlockMapper } from '~/mappers/ldsBlock';
import { linksListMapper } from '~/mappers/linksList';
import { LoginRegisterBlocksMapper } from '~/mappers/LoginRegisterBlocks';
import { mediaPlusTextBlockMapper } from '~/mappers/mediaPlusTextBlock';
import { multiVariantSelectorMapper } from '~/mappers/multiVariantSelector';
import { newsletterMapper } from '~/mappers/newsletter';
import { pimAssetsCarouselMapper } from '~/mappers/pimAssetsCarousel';
import { productComparisonMapper } from '~/mappers/productComparison';
import { promoFormMapper } from '~/mappers/promoForm';
import { promoQuizMapper } from '~/mappers/promoQuiz';
import { retailersBlockMapper } from '~/mappers/retailers';
import { salsifyColumnsTextMapper } from '~/mappers/salsifyColumnsText';
import { salsifyTextMapper } from '~/mappers/salsifyText';
import { searchResultsMapper } from '~/mappers/searchResults';
import { secondaryNavigationMapper } from '~/mappers/secondaryNavigation';
import { sitemapMapper } from '~/mappers/sitemap';
import { splitterMapper } from '~/mappers/splitter';
import { tabsMapper } from '~/mappers/tabs';
import { teaserMapper } from '~/mappers/teaser';
import { traitGroupMapper } from '~/mappers/traitGroup';
import { traitGroupPIMMapper } from '~/mappers/traitGroupPIM';
import { typographyMapper } from '~/mappers/typography';
import { variantSelectorMapper } from '~/mappers/variantSelector';
import { whereToBuyMapper } from '~/mappers/whereToBuy';

import { PAGE_ONLY_FOR_SIGNED_IN } from '../LoginRegisterBlocks/constants';
import { BodyRenderProps } from './models';

const AccordionList = lazy(() => import('~/components/AccordionList'));
const DebugComponent = lazy(
  () => import('~/components/BodyRender/DebugComponent')
);
const ContactUs = lazy(() => import('~/components/ContactUsBlock'));
const Iframe = lazy(() => import('~/components/Iframe'));
const ImagePlusTextBlock = lazy(
  () => import('~/components/ImagePlusTextBlock')
);
const ImageTextBlock = lazy(() => import('~/components/ImageTextBlock'));
const ItemListing = lazy(() => import('~/components/ItemListing'));
const LdsBlock = lazy(() => import('~/components/LdsBlock'));
const LoginRegisterBlocks = lazy(
  () => import('~/components/LoginRegisterBlocks')
);
const MediaPlusTextBlock = lazy(
  () => import('~/components/MediaPlusTextBlock')
);
const NewsletterForm = lazy(() => import('~/components/NewsletterForm'));
const ProductComparison = lazy(() => import('~/components/ProductComparison'));
const PromoForm = lazy(() => import('~/components/PromoForm'));
const PromoQuiz = lazy(() => import('~/components/PromoQuiz'));
const RetailersPageBlock = lazy(
  () => import('~/components/RetailersPageBlock')
);
const SalsifyColumnsText = lazy(
  () => import('~/components/SalsifyColumnsText')
);
const SearchResults = lazy(() => import('~/components/SearchResults'));
const SitemapList = lazy(() => import('~/components/SitemapList'));
const TabsBlock = lazy(() => import('~/components/TabsBlock'));

const ImageBlock = lazy(() => import('~/components/ImageBlock'));
const Splitter = lazy(() => import('~/components/Splitter'));
const JsonRTEBlock = lazy(() => import('~/components/JsonRTEBlock'));
const LinksList = lazy(() => import('~/components/LinksList'));
const PIMAssetsCarousel = lazy(() => import('~/components/PIMAssetsCarousel'));
const CountryPickerBlock = lazy(
  () => import('~/components/CountryPickerBlock')
);

export const typesMapper = {
  default: (typeName) => [
    forwardRef<HTMLDivElement, object>((item, ref) =>
      process.env.NODE_ENV === 'development' ? (
        <DebugComponent typeName={typeName} {...item} ref={ref} />
      ) : null
    ),
    (item) => item,
  ],
  ItemListing: [ItemListing, itemListingMapper],
  Teaser: [Teaser, teaserMapper],
  ContactUs: [ContactUs, contactUsBlockMapper],
  ImageTextBlock: [
    ImageTextBlock,
    imageTextBlockMapper,
    ({ items, componentId, pageBlockUI }) => ({
      items,
      componentId,
      pageBlockUI,
    }),
  ],
  TextBlock: [Typography, ({ typography }) => typographyMapper(typography)],
  Typography: [Typography, typographyMapper],
  Image: [ImageBlock, (image: Image) => imageBlockMapper({ images: [image] })],
  SalsifyText: [PageBlock, salsifyTextMapper],
  SalsifyColumnsText: [SalsifyColumnsText, salsifyColumnsTextMapper],
  ImageBlock: [ImageBlock, imageBlockMapper],
  ImagePlusTextBlock: [ImagePlusTextBlock, imagePlusTextBlockMapper],
  MediaPlusTextBlock: [MediaPlusTextBlock, mediaPlusTextBlockMapper],
  LdsBlock: [LdsBlock, ldsBlockMapper],
  SitemapBlock: [SitemapList, sitemapMapper],
  Iframe: [Iframe, iframeMapper],
  RetailersBlock: [RetailersPageBlock, retailersBlockMapper],
  SearchResults: [SearchResults, searchResultsMapper],
  Newsletter: [NewsletterForm, newsletterMapper],
  PromoForm: [PromoForm, promoFormMapper],
  PromoQuiz: [PromoQuiz, promoQuizMapper],
  BazaarVoiceReviews: [
    BazaarVoiceReviews,
    (items) => items,
    (customValue) => ({
      productId: customValue.productId,
      componentId: customValue.componentId,
    }),
  ],
  Accordion: [AccordionList, accordionMapper],
  LoginRegister: [LoginRegisterBlocks, LoginRegisterBlocksMapper],
  Tabs: [TabsBlock, tabsMapper],
  ProductComparison: [ProductComparison, productComparisonMapper],
  SecondaryNavigation: [SecondaryNavigation, secondaryNavigationMapper],
  Splitter: [Splitter, splitterMapper],
  WhereToBuy: [WhereToBuy, whereToBuyMapper],
  VariantSelector: [VariantSelector, variantSelectorMapper],
  MultiVariantSelector: [VariantSelector, multiVariantSelectorMapper],
  JsonRTEBlock: [
    JsonRTEBlock,
    jsonRTEBlockMapper,
    ({ rte, componentId, pageBlockUI }) => ({
      rte,
      componentId,
      pageBlockUI,
    }),
  ],
  TraitGroup: [TraitGroup, traitGroupMapper],
  LinksList: [
    LinksList,
    linksListMapper,
    ({ pageBlockUI, componentId, variant, size, items }) => ({
      pageBlockUI,
      componentId,
      variant,
      size,
      items,
    }),
  ],
  PIMAssetsCarousel: [
    PIMAssetsCarousel,
    pimAssetsCarouselMapper,
    (props) => props,
  ],
  TraitGroupPIM: [TraitGroup, traitGroupPIMMapper],
  CountryPicker: [CountryPickerBlock, countryPickerMapper],
};

const BodyRender: FC<BodyRenderProps> = ({
  items,
  currentPageUid,
  externalData,
}) => {
  const customComponents = useCustomComponents();

  const typesMapperWithCustomComponents = {
    ...customComponents.reduce((acc, { key, component }) => {
      acc[key] = [component];

      return acc;
    }, {}),
    ...typesMapper,
  };

  const renderedItems = items.map((item) => {
    const value = Object.values(item).find((val) => val);

    return value || { typeName: 'Unknown component', item: [] };
  });

  const onlyForSignedIn =
    process.env.GATSBY_LOGIN_REGISTER_ENABLED === 'true' &&
    renderedItems.find(
      (item) =>
        item.typeName === 'LoginRegister' &&
        (item.item[0] as { variant?: string })?.variant === 'only_signed_in'
    );

  const Wrapper = onlyForSignedIn ? LoginRegisterBlocks : Fragment;

  return (
    <Wrapper {...(onlyForSignedIn ? { variant: PAGE_ONLY_FOR_SIGNED_IN } : {})}>
      {renderedItems?.map((allValues, index) => {
        let { typeName } = allValues;
        const { item } = allValues;

        const originalTypeName = typeName;

        if (['KeyValue', 'CustomContentModel'].includes(typeName)) {
          // eslint-disable-next-line camelcase
          typeName = (item as { component_id: string }[])[0]?.component_id;
        }

        const [ItemComponent, itemMapper, customValue] =
          typesMapperWithCustomComponents[typeName] ||
          typesMapperWithCustomComponents.default(typeName);

        const key = `${typeName}__${index}`;
        let value = Array.isArray(item) ? item[0] : item;

        if (customValue) {
          value = customValue(allValues);
        }

        if (typeName === 'Tabs') {
          value = { ...(value as {}), currentPageUid };
        }

        let itemMapperFn = itemMapper;

        if (originalTypeName === 'KeyValue') {
          itemMapperFn = keyValueMapper;
        }

        return (
          <ElementOnScreen
            key={key}
            componentName={typeName}
            render={(ref) => (
              <ItemComponent
                ref={ref}
                key={key}
                {...(itemMapperFn
                  ? itemMapperFn(value, currentPageUid, externalData)
                  : value)}
              />
            )}
          />
        );
      })}
    </Wrapper>
  );
};

export default BodyRender;
